import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import Moment from 'react-moment';
import EnhancedTable from '../../components/table';
import CatalogManagerResults from '../../components/CatalogManagerResults';
import DownloadButton from '../../components/download_button';
import { Root, Header, defaultResultsContainerStyles, pageContentGap } from '../../theme';
import { getCompanyNames } from '../../helpers';
import Config from '../../config';

// history/location come from react-router v5's Route render-prop. Typed via
// the `history` package because @types/react-router-dom's RouteComponentProps
// doesn't compose cleanly here.
export interface AttachmentsV2Props {
  history?: import('history').History;
  location?: import('history').Location;
  setTitle?: (title: string) => void;
}

// All fields optional — column templates receive partial rows from CMR.
export interface AttachmentRow {
  Id?: number;
  CreateDate?: string;
  CompanyName?: string;
  PartnerCompanyName?: string;
  Description?: string;
  Type?: string;
  Size?: number | null;
  BlobPath?: string;
  Duplicate?: boolean;
}

// Loose by design — the shared table library exports no column type and
// column shape varies across pages.
export interface AttachmentColumn {
  id: string;
  hidden?: boolean;
  toggleable?: boolean;
  type?: string;
  filterable?: boolean;
  sortable?: boolean;
  label?: string;
  width?: string;
  numeric?: boolean;
  allowStringOperators?: boolean;
  getOptions?: (...args: any[]) => any;
  template?: (row: AttachmentRow) => React.ReactNode;
}

// styled(Typography) strips the OverridableComponent typing so the `component`
// prop isn't visible without this cast.
const PageHeading = Header as React.ComponentType<any>;

// EnhancedTable is `withRouter(class EnhancedTable extends React.Component)` —
// withRouter's TS overload narrows the wrapped props to `Pick<RouteComponentProps, never>`,
// hiding the actual `config`/`template`/`history` surface. CatalogManagerResults
// is also a .js default export with no explicit Props interface. Cast both to
// permissive component types so the .tsx file builds under fork-ts-checker.
const TypedEnhancedTable = EnhancedTable as React.ComponentType<any>;
const TypedCatalogManagerResults = CatalogManagerResults as React.ComponentType<any>;

// CatalogManagerResults invokes column.template as (row, column, ...) —
// templates pull cell values off `row[column.id]`, NOT a leading `value` arg.
export const ATTACHMENT_COLUMNS: AttachmentColumn[] = [
  { id: 'Id', hidden: true },
  {
    id: 'CreateDate',
    type: 'date',
    filterable: true,
    sortable: true,
    label: 'Date',
    template: (row: AttachmentRow) => <Moment format="MM/DD/YYYY hh:mm A">{row?.CreateDate}</Moment>,
    width: '11em',
  },
  // CreateDate stays non-toggleable — it's the primary sort key (matches
  // order_list_v2's treatment of `date`).
  { id: 'CompanyName', filterable: true, toggleable: true, label: 'Sender', width: '15em', getOptions: getCompanyNames },
  { id: 'PartnerCompanyName', filterable: true, toggleable: true, label: 'Receiver', width: '15em', getOptions: getCompanyNames },
  { id: 'Description', filterable: true, toggleable: true, label: 'Description', allowStringOperators: true },
  { id: 'Type', filterable: true, toggleable: true, label: 'Type', width: '8em' },
  {
    id: 'Size',
    sortable: true,
    toggleable: true,
    label: 'Size',
    numeric: true,
    width: '5em',
    template: (row: AttachmentRow) => convertSize(row?.Size ?? null),
  },
  {
    // filterable: false keeps the download-button column out of the
    // CatalogFilters field-picker (which defaults to "include unless false").
    id: 'BlobPath',
    filterable: false,
    width: '10em',
    template: (row: AttachmentRow) => (
      <DownloadButton size="small" variant="outlined" href={row?.BlobPath}>
        Download
      </DownloadButton>
    ),
  },
];

// /odata/Company/Attachments doesn't honor &term=, so the search bar is
// hijacked into a `contains()` $filter over these fields.
export const SEARCH_FIELDS = ['CompanyName', 'PartnerCompanyName', 'Description'] as const;

// Layered on top of CatalogFilters' internal debounce to keep the total
// fetch count at one per typing-cluster.
export const SEARCH_DEBOUNCE_MS = 300;

// Default lookback so we don't pull the entire historical attachment table
// on first load. Non-system so it shows as a removable chip.
export const DEFAULT_LOOKBACK_DAYS = 14;

export function getDefaultLookbackIso(now: Date = new Date()): string {
  const lookback = new Date(now);
  lookback.setDate(lookback.getDate() - DEFAULT_LOOKBACK_DAYS);
  return lookback.toISOString();
}

// CatalogFilters bumps {term, refresh} together on every typed cluster, which
// would make table.tsx refetch via the refresh branch. Unflip refresh and stamp
// _skipRefresh so neither branch fires — our filter useEffect flips refresh
// itself for the single consolidated fetch.
export function suppressTermRefetch(prev: any, next: any): any {
  if (next?.term !== prev?.term) {
    return { ...next, refresh: prev?.refresh, _skipRefresh: true };
  }
  return next;
}

// Strips system filters before rebuilding so re-running on every term change
// doesn't leak the previous contains-compound. User-added chips survive.
export function buildAttachmentFilter(
  term: string | undefined,
  existingFilters: any[] | undefined,
): { logic: 'and'; filters: any[] } {
  const userFilters = (existingFilters || []).filter((f: any) => !f?.system);
  const filters: any[] = [
    ...userFilters,
    { field: 'Duplicate', operator: 'eq', value: false, system: true },
  ];

  const trimmed = (term || '').trim();
  if (trimmed) {
    filters.push({
      logic: 'or',
      filters: SEARCH_FIELDS.map((field) => ({
        field,
        operator: 'contains',
        value: trimmed,
        system: true,
      })),
      system: true,
    });
  }

  return { logic: 'and', filters };
}

export function convertSize(bytes: number | null | undefined): string | null {
  // Return null instead of letting "NaN B" / "Infinity B" / "-1 B" reach the cell.
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
    return null;
  }
  if (bytes / (1024 * 1024) > 1) {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  } else if (bytes / 1024 > 1) {
    return (bytes / 1024).toFixed(2) + ' KB';
  }
  return bytes + ' B';
}

function AttachmentsV2(props: AttachmentsV2Props) {
  const { setTitle, history, location } = props;

  useEffect(() => {
    if (setTitle) setTitle('Attachments');
  }, [setTitle]);

  const [config, setConfig] = useState<any>(() => {
    const lookbackFilter = {
      field: 'CreateDate',
      operator: 'ge',
      value: getDefaultLookbackIso(),
    };
    return {
      url: Config.api + '/odata/Company/Attachments',
      columns: ATTACHMENT_COLUMNS,
      order: 'desc',
      orderBy: 'Id',
      keyField: 'Id',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      filterInUrl: true,
      title: 'Attachments',
      disableTermParam: true,
      filter: buildAttachmentFilter(undefined, [lookbackFilter]),
    };
  });



  return (
    <main aria-labelledby="page-title">
      <Root style={{ gap: pageContentGap, padding: '24px 14px 24px 14px' }}>
        <Grid container justifyContent="space-between" direction="row" alignItems="center" style={{ width: '100%' }}>
          <Grid item>
            <PageHeading id="page-title" component="h1">
              Attachments
            </PageHeading>
          </Grid>
        </Grid>
        <Grid style={{ width: '100%' }}>
          <TypedEnhancedTable
            config={config}
            history={history}
            location={location}
            template={(table: any) => (
              <TypedCatalogManagerResults
                isSelectable={false}
                disableSearch={true}
                disableTermInUrl={true}
                condensed={false}
                nonCondensedRows={true}
                showSavedSearch={true}
                savedSearchTableType="attachments"
                history={history}
                location={location}
                table={table}
                config={config}
                setConfig={setConfig}
                resultsContainerStyles={defaultResultsContainerStyles}
                renderButtonFiltersAfter={false}
              />
            )}
          />
        </Grid>
      </Root>
    </main>
  );
}

export default AttachmentsV2;
